import { useEffect, useMemo, useRef } from 'react'

import { ContactOverlay } from '#app/routes/mocra+/_form+/components/contacts/overlay.tsx'
import {
	type ContactCandidate,
	type ContactDraft,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'
import {
	combineCandidateLists,
	createCandidateSignature,
} from '#app/routes/mocra+/_form+/components/contacts/utils.ts'
import { useContactDirectory } from '#app/routes/mocra+/_form+/hooks/use-contact-directory.ts'

type Props = {
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: ContactDraft) => void
	readonly initialValue: ContactDraft
	readonly candidates?: readonly ContactCandidate[]
	readonly title?: string
	readonly description?: string
	readonly submitLabel?: string
	readonly cancelLabel?: string
	readonly onCancel?: () => void
	readonly onClosed?: () => void
	readonly directorySource?: string
	readonly existingContacts?: readonly ContactDraft[]
}

export function ContactEditOverlay({
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	candidates,
	title,
	description,
	submitLabel,
	cancelLabel,
	onCancel,
	onClosed,
	directorySource,
	existingContacts,
}: Props) {
	const {
		candidates: directoryCandidates,
		mergeContacts,
		upsertContact,
	} = useContactDirectory()
	const mergeSignatureRef = useRef<string | null>(null)

	useEffect(() => {
		if (!candidates?.length) {
			mergeSignatureRef.current = null
			return
		}
		const signature = candidates
			.map((candidate) => createCandidateSignature(candidate))
			.join('||')
		if (mergeSignatureRef.current === signature) return
		mergeSignatureRef.current = signature
		mergeContacts(candidates, { source: directorySource })
	}, [candidates, mergeContacts, directorySource])

	const combinedCandidates = useMemo(
		() => combineCandidateLists(directoryCandidates, candidates),
		[directoryCandidates, candidates],
	)

	const excludedEmails = useMemo(() => {
		if (!existingContacts?.length) return []
		return existingContacts
			.map((contact) => contact.email?.trim())
			.filter((email): email is string => Boolean(email && email.length))
	}, [existingContacts])

	const handleSubmit = (draft: ContactDraft) => {
		const entry = upsertContact(draft, { source: directorySource })
		const resolvedDraft: ContactDraft = {
			id: entry?.id ?? draft.id,
			title:
				draft.title?.trim().length > 0
					? draft.title.trim()
					: (entry?.title ?? draft.title ?? ''),
			name: entry?.name ?? draft.name.trim(),
			email: entry?.email ?? draft.email.trim(),
			phone: entry?.phone ?? draft.phone?.trim() ?? '',
		}
		onSubmit(resolvedDraft)
	}

	return (
		<ContactOverlay
			mode="edit"
			open={open}
			onOpenChange={onOpenChange}
			onSubmit={handleSubmit}
			initialValue={initialValue}
			candidates={combinedCandidates}
			title={title}
			description={description}
			submitLabel={submitLabel}
			cancelLabel={cancelLabel}
			onCancel={onCancel}
			onClosed={onClosed}
			excludedEmails={excludedEmails}
		/>
	)
}
